import { HttpClient } from "@angular/common/http";
import { Injectable, Injector, OnDestroy } from "@angular/core";
import { Firestore } from "@angular/fire/firestore";
import { MatDialog } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { BehaviorSubject, interval, Observable, of, Subscription } from "rxjs";
import { catchError, delay, take } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppUpdateComponent } from "../components/app-update/app-update.component";
import { AppVersionService } from "./appVersions/app-version.service";
import { CommonService } from "./common/common.service";
import { SWServiceService } from "./serviceWorkers/swservice.service";
import { SafeLocalStorageService } from "./safe-local-storage.service";
import { NativeSwUpdateService } from "./native-sw-update.service";
@Injectable({
  providedIn: "root",
})
export class UpdatePWAService implements OnDestroy {
  private updatePopUpAlreadyThere = false;
  selectedLanguage = "en";
  private updateCheckSubscription: Subscription;
  private updatedVersionData: Data;
  private firstFetchSubject = new BehaviorSubject<boolean>(true);
  appVersionMajor: number;
  appVersionMinor: number;
  userVersionMajor: number;
  userVersionMinor: number;
  appVersionPatch: number;
  userVersionPatch: number;
  lastLoginDate: Date;
  discritLink: string;
  discritLink$: Observable<string>;
  userPWAVersion: string;
  latestAppVersion: string;
  private versionCheckAfterLogin = false;
  private delayFiveMin = 300000;
  private userFingerPrintId: string;
  private appVersionSubscription: Subscription;
  private userVersionSubscription: Subscription;

  // Phase 5 Fix: Proper 6-hour interval constant
  private readonly SIX_HOURS_MS = 6 * 60 * 60 * 1000;
  private readonly INITIAL_DELAY_MS = 20000;
  private initialTimeoutId: ReturnType<typeof setTimeout>;
  private visibilityChangeListener: () => void;

  get pwaVersionPath(): string {
    return environment.pwaVersionCheckPath;
  }

  constructor(
    private firestore: Firestore,
    private injector: Injector,
    private matDialog: MatDialog,
    private router: Router,
    private http: HttpClient,
    private swservice: SWServiceService,
    private appversion: AppVersionService,
    private commonService: CommonService,
    private safeLocalStorage: SafeLocalStorageService,
    private nativeSwUpdate: NativeSwUpdateService
  ) {
    this.getHash();
    this.selectedLanguage = this.safeLocalStorage.getItem("language_code") || "en";
    this.initializeUpdateCheckInterval();
  }

  /**
   * Phase 5 Fix: Proper 6-hour update check with foreground visibility check
   * - Runs every 6 hours (21,600,000 ms = 6 * 60 * 60 * 1000)
   * - Only executes when page is visible (foreground)
   * - Calls appversion.checkForUpdates() for actual update detection
   */
  private initializeUpdateCheckInterval(): void {
    this.initialTimeoutId = setTimeout(() => {
      if ("serviceWorker" in navigator) {
        console.log('[UpdatePWA] Initializing 6-hour update check interval');
        
        // Phase 5 Fix: First check after initial delay
        this.performUpdateCheck();

        // Phase 5 Fix: Recurring check every 6 hours only when visible
        this.updateCheckSubscription = interval(this.SIX_HOURS_MS).subscribe(() => {
          this.performUpdateCheck();
        });

        // Phase 5 Fix: Listen for visibility changes to resume checks when returning to foreground
        this.visibilityChangeListener = () => {
          if (document.visibilityState === 'visible') {
            console.log('[UpdatePWA] Page became visible, performing update check');
            this.performUpdateCheck();
          }
        };
        document.addEventListener('visibilitychange', this.visibilityChangeListener);
      }
    }, this.INITIAL_DELAY_MS);
  }

  /**
   * Phase 5 Fix: Perform actual update check with proper error handling
   * Delegates to appversion.checkForUpdates() which uses semver comparison
   */
  private performUpdateCheck(): void {
    if (document.visibilityState !== 'visible') {
      console.log('[UpdatePWA] Skipping check - page not visible');
      return;
    }

    console.log('[UpdatePWA] Checking for updates at', new Date().toISOString());
    
    this.appversion.checkForUpdates()
      .catch((err) => {
        console.error('[UpdatePWA] Update check failed:', err);
      });
  }

  getHash() {
    try {
      this.http
        .get("assets/sw-hash.txt", { responseType: "text" })
        .pipe(
          catchError((error) => {
            return of({ success: false, code: "Failed to fetch hash", message: "Failed to fetch hash" });
          })
        )
        .subscribe((hash: string) => {
          if (hash) {
            this.safeLocalStorage.setItem("sw_hash", hash);
          }
        });
    } catch (e) {
      return of({ success: false, code: "Failed to fetch hash", message: "Failed to fetch hash" });
    }
  }

  public promptUser(event?: Data) {
    if (this.updatePopUpAlreadyThere) {
      return;
    }
    this.updatePopUpAlreadyThere = true;

    const hashData = this.safeLocalStorage.getItem("sw_hash");

    this.commonService.getUid("UpdatePWAService").pipe(take(1)).subscribe({
      next: (userId) => {
        if (userId && userId.length > 6 && event) {
          const path_id = "yzF1Ss5eiMqnh91zQMtv";
          const doc_data = { sw_hash: hashData };
          const method = "update";
          const authService = this.injector.get(AuthService);
          authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            method,
          }).subscribe({
            error: (error) => {
              console.error("[UpdatePWA] Error updating Firestore document:", error);
            }
          });
        }
      },
      error: (err) => {
        console.error("[UpdatePWA] Error fetching userId:", err);
      },
    });

    const decline = this.matDialog.open(AppUpdateComponent, {
      panelClass: "app-update-dialog-size",
      backdropClass: "decline-back-drop-color",
      disableClose: true,
    });

    decline
      .afterClosed()
      .pipe(take(1), delay(3000))
      .subscribe(async () => {
        try {
          await this.appversion.updateUserPwaNodes();
          this.versionCheckAfterLogin = false;

          await this.appversion.updatePwaVersion(this.updatedVersionData);

          // Activate waiting SW before unregister so SKIP_WAITING can still reach it.
          try {
            await this.nativeSwUpdate.activateUpdate();
          } catch (activateErr) {
            console.warn("[UpdatePWA] SW activate failed, continuing reload:", activateErr);
            try {
              const authService = this.injector.get(AuthService);
              const translatedMsg = await authService.getConstantsMsg("1088");
              const defaultMsg = {
                message: {
                  line_1: "We are updating the app, please wait!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle info.svg",
                  type: "Info",
                },
              };
              authService.showToastMessage(translatedMsg, defaultMsg);
            } catch {
              // toast best-effort
            }
          }

          if ("caches" in self) {
            try {
              const keyList = await caches.keys();
              await Promise.all(keyList.map((key) => caches.delete(key)));
            } catch (err) {
              console.error("[UpdatePWA] Error deleting caches:", err);
            }
          } else {
            console.warn("[UpdatePWA] Caches API is not supported in this environment.");
          }

          this.swservice.unRegisterServiceWorker();
          document.location.reload();
        } catch (err) {
          console.error("[UpdatePWA] Update failed:", err);
          this.updatePopUpAlreadyThere = false;
        }
      });
  }

  ngOnDestroy(): void {
    // Clear initial timeout
    if (this.initialTimeoutId) {
      clearTimeout(this.initialTimeoutId);
    }

    // Unsubscribe from interval
    if (this.updateCheckSubscription) {
      this.updateCheckSubscription.unsubscribe();
    }

    // Remove visibility change listener
    if (this.visibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    }
  }
}
